import { useEffect, type ReactNode } from "react";
import { m } from "motion/react";
import { brl, type PdvCheckoutResult } from "@vertex/validators/pdv-pure";
import { MarcaDeOk, NumeroAnimado, useMovimento } from "@vertex/ui/movimento";
import { buttonClass } from "@vertex/ui/primitives/button-style";
import type { PdvContext } from "../lib/pdv";

/**
 * Venda concluída. O número que aparece grande é o `docNumber` — é o que o
 * cliente cita quando volta na loja. Na venda offline (Fase 6) ele vem como
 * `OFF-…` provisório; **nunca inventar um `PED-` aqui**, o cliente citaria um
 * número que jamais existirá.
 *
 * VOS Movimento (26/09): a venda registrada é um marco (pagamento
 * confirmado), com o desfecho do sistema que cabe no terminal: o disco verde
 * pousa na mola `pouso` (o único quique do VOS), o ✓ se desenha e o anel se
 * desfaz uma vez (`MarcaDeOk disco anel`); o título, o número e o valor pago
 * sobem 8px na cascata, e o valor CONTA até o número exato. (O
 * `MomentoDeSucesso` completo mora no web; aqui fica o que o orçamento do
 * terminal paga sem nada novo.)
 */
export function Receipt({
  result,
  ctx,
  onNewSale,
}: {
  result: PdvCheckoutResult;
  ctx: PdvContext;
  onNewSale: () => void;
}) {
  const pedido = result.order ?? result.pickupOrder;
  const mov = useMovimento();
  const sobe = (i: number) => {
    const r = mov.r.item(i);
    return { initial: r.initial, animate: r.animate };
  };
  /* Conta em reais inteiros e crava o valor exato no fim (sem centavo
     piscando na cauda da mola), a régua do momento de sucesso. */
  const pagoAgora = result.paidNow;
  const contando = (n: number) => brl(Math.abs(pagoAgora - n) < 1 ? pagoAgora : Math.round(n));

  /* Qualquer tecla volta pra venda: o operador não precisa achar o botão com o
     próximo cliente já no balcão. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Enter" || e.key === "Escape" || e.key === " ") onNewSale();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNewSale]);

  useEffect(() => {
    if (ctx.autoPrint) window.print();
  }, [ctx.autoPrint]);

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div
        className="w-full max-w-[420px] rounded-xl p-8 text-center"
        style={{ background: "var(--panel)", border: "1px solid var(--line)", boxShadow: "var(--shadow-lg)" }}
      >
        <span className="mx-auto mb-3 flex w-fit" aria-hidden>
          <MarcaDeOk tamanho={44} disco anel />
        </span>
        <m.h1 className="mb-1 font-semibold" style={{ fontSize: "var(--fs-h3)" }} {...sobe(1)}>
          Venda Registrada
        </m.h1>
        <m.p className="mb-6" style={{ fontSize: "var(--fs-meta)", color: "var(--ink-3)" }} {...sobe(2)}>
          {pedido?.docNumber ?? pedido?.number ?? "Comprovante indisponível"}
        </m.p>

        <m.dl className="mb-6 grid gap-2 text-left" {...sobe(3)}>
          <Linha
            rotulo="Pago agora"
            valor={
              <>
                <span className="sr-only">{brl(pagoAgora)}</span>
                <span aria-hidden>
                  <NumeroAnimado valor={pagoAgora} formatar={contando} modo="contar" contarNaChegada />
                </span>
              </>
            }
            destaque
          />
          {result.dueOnPickup > 0 ? (
            <Linha rotulo="A receber na retirada" valor={brl(result.dueOnPickup)} />
          ) : null}
          {result.cardFee ? (
            <>
              <Linha
                rotulo={`Taxa da maquininha (${result.cardFee.installments}×)`}
                valor={brl(result.cardFee.feeAmount)}
              />
              <Linha rotulo="Líquido" valor={brl(result.cardFee.net)} />
            </>
          ) : null}
        </m.dl>

        {result.cardFee?.semRegra ? (
          <p
            className="mb-4 rounded-lg px-3 py-2 text-left"
            style={{ fontSize: "var(--fs-caption)", color: "var(--warn)", background: "var(--warn-soft)" }}
          >
            A tabela da maquininha não cobre esta combinação, então a taxa ficou zerada. O lucro
            desta venda vai aparecer maior do que foi.
          </p>
        ) : null}

        <button type="button" autoFocus className={`${buttonClass("primary", "lg")} w-full`} onClick={onNewSale}>
          Nova Venda
        </button>
      </div>
    </div>
  );
}

function Linha({ rotulo, valor, destaque }: { rotulo: string; valor: ReactNode; destaque?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt style={{ fontSize: "var(--fs-meta)", color: "var(--ink-2)" }}>{rotulo}</dt>
      <dd style={{ fontSize: destaque ? "var(--fs-body)" : "var(--fs-meta)", fontWeight: destaque ? 700 : 400 }}>
        {valor}
      </dd>
    </div>
  );
}
